import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { setShowFreeSpinOutro } from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
// Temporarily disabled — spine centered at screen center
// import { GAME_CONTAINER_OFFSET_CONFIG } from "../../../../Games/Main/GameContainer/GameContainer";

// Device and orientation-specific size configuration
const OUTRO_SIZE_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.85,
      buttonFontScale: 0.5,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.05,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.75,
      buttonFontScale: 0.65,
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.2,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 0.65,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 0.65,
    },
  },
  DESKTOP: {
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.0,
      buttonFontScale: 1.1,
    },
  },
};

/**
 * FreeSpinOutroDialogController
 * Handles the free spin outro dialog: in → idle → (user click) → out
 * Follows the EXACT same imperative pattern as FreeSpinIntroDialogController.
 * Text slots: txt_slot_total_win, txt_slot_total_value, txt_slot_click_to
 */
class FreeSpinOutroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.outroSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state (matching intro pattern)
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;

    // Button attached to spine slot (matching intro pattern)
    this.buttonContainer = null;
    this.buttonShadowText = null;
    this.buttonText = null;

    // Text containers for cleanup
    this.textContainers = null;

    // Internal caches for performance optimization
    this._configCache = null;
    this._deviceScaleMultiplierCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeOutroSpine();
  }

  // ===================================================================
  //  CONFIG / SCALING (same as intro)
  // ===================================================================

  getConfig() {
    const { device, orientation } = this.dimensionsContext;

    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._configCache;
    }

    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) deviceKey = "MOBILE";
    else if (device === PixiSettings.DEVICE.TABLET) deviceKey = "TABLET";
    else deviceKey = "DESKTOP";

    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    const deviceConfig =
      OUTRO_SIZE_CONFIG[deviceKey] || OUTRO_SIZE_CONFIG.DESKTOP;
    const config =
      deviceConfig[orientationKey] ||
      deviceConfig.LANDSCAPE ||
      OUTRO_SIZE_CONFIG.DESKTOP.LANDSCAPE;

    this._configCache = config;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    return config;
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * (Same as intro's applyScaleAndCenter)
   */
  applyScaleAndCenter() {
    if (!this.outroSpine || !this.dimensions) return;

    const config = this.getConfig();
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    scale *= config.spineScaleMultiplier;

    this.outroSpine.scale.set(scale);
    this.outroSpine.x = 0;

    // Temporarily disabled — center spine at screen center
    // const { device, orientation } = this.dimensionsContext;
    // const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[device];
    // const reelOffset =
    //   typeof deviceConfig === "number"
    //     ? deviceConfig
    //     : (deviceConfig?.[orientation] ?? 0.2);
    // this.outroSpine.y = -this.symbolHeight * reelOffset + (config.spineYOffset || 0);
    this.outroSpine.y = config.spineYOffset || 0;

    // Update standalone button position
    this.updateButtonPosition();
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * (Same as intro's updateDimensions)
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._configCache = null; // Force getConfig() to recalculate
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text and style if device/orientation changed
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }
    this.updateButtonStyle();

    if (
      this.outroSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  // ===================================================================
  //  SPINE CREATION (same pattern as intro)
  // ===================================================================

  initializeOutroSpine() {
    try {
      this.outroSpine = this.createOutroSpine();

      if (!this.outroSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.outroSpine.skeleton) missingComponents.push("skeleton");
      if (!this.outroSpine.state) missingComponents.push("state");
      if (!this.outroSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.outroSpine);
      this.outroSpine.alpha = 0.001;
      this.outroSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.outroSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      this.warmUpOutroSpine();

      this.initializeButton();
      this.initializeTexts();
    } catch {
      // Silent fail
    }
  }

  createOutroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "dialogs-skeleton";
      const atlasAlias = "dialogs-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const outroSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!outroSpine?.skeleton?.data?.animations) {
        return null;
      }

      return outroSpine;
    } catch {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * (Same as intro's warmUpIntroSpine — does NOT clearTracks after)
   */
  warmUpOutroSpine() {
    if (!this.outroSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.outroSpine.state.setAnimation(0, animations[0], false);
        this.outroSpine.state.update(0.016);
        this.outroSpine.skeleton.updateWorldTransform();
      }
      this.outroSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.outroSpine?.skeleton?.data?.animations) return [];

    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    const animations = this.outroSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  // ===================================================================
  //  BUTTON (overlay-positioned, NOT attached to spine slot)
  // ===================================================================

  /**
   * Initialize tap-to-continue button as a standalone overlay element.
   * Positioned using symbolHeight * buttonYOffsetMultiplier for per-device control.
   */
  initializeButton() {
    if (!this.outroSpine || !this.outroSpine.skeleton) {
      return;
    }

    // Create container as direct child of overlay (NOT attached to spine slot)
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "free_spin_outro_continue_overlay";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001;

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Two-layer PRIMARY text (shadow + top)
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const config = this.getConfig();
    const fontSize = Math.round(32 * (config.buttonFontScale || 1.0));
    const scaled = this._computeScaledStrokeParams(fontSize);
    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
    };

    // Shadow layer — scale strokeThickness proportionally to font size
    this.buttonShadowText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.shadow,
        strokeThickness: scaled.shadowStroke,
      }),
    );
    this.buttonShadowText.resolution = slotText.resolution;
    this.buttonShadowText.anchor.set(0.5);
    this.buttonShadowText.y = scaled.shadowOffsetY;

    // Top layer — scale strokeThickness proportionally to font size
    this.buttonText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.top,
        strokeThickness: scaled.topStroke,
      }),
    );
    this.buttonText.resolution = slotText.resolution;
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonShadowText);
    this.buttonContainer.addChild(this.buttonText);

    // Add to overlay container directly (not to spine slot)
    this.container.addChild(this.buttonContainer);

    // Position based on device config
    this.updateButtonPosition();
  }

  /**
   * Update button position based on current dimensions and device config.
   * Button is positioned at bottom of screen using symbolHeight * multiplier offset.
   * Called on init and on every resize.
   */
  updateButtonPosition() {
    if (!this.buttonContainer || !this.dimensions) return;

    const config = this.getConfig();
    const multiplier = config.buttonYOffsetMultiplier ?? 1.0;

    // Container is centered at screen center, so y=0 = center
    // Position button below center: half screen height minus one symbolHeight * multiplier
    const buttonY = this.dimensions.height / 2 - this.symbolHeight * multiplier;

    this.buttonContainer.x = 0;
    this.buttonContainer.y = buttonY;
  }

  /**
   * Update button text style (font size) when device/orientation changes.
   * Recalculates fontSize from the current device config's buttonFontScale.
   */
  updateButtonStyle() {
    if (!this.buttonText || !this.buttonShadowText) return;

    const config = this.getConfig();
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const fontSize = Math.round(32 * (config.buttonFontScale || 1.0));
    const scaled = this._computeScaledStrokeParams(fontSize);

    // Update shadow text style — scale stroke proportionally
    this.buttonShadowText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.shadow,
      strokeThickness: scaled.shadowStroke,
    });
    this.buttonShadowText.y = scaled.shadowOffsetY;

    // Update top text style — scale stroke proportionally
    this.buttonText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.top,
      strokeThickness: scaled.topStroke,
    });
  }

  /**
   * Scale stroke thickness and shadow offset proportionally to the computed font size.
   * At the base font size (32px) the values match the raw PRIMARY style.
   * On smaller devices the ratio shrinks, keeping layers tight.
   */
  _computeScaledStrokeParams(fontSize) {
    const baseFontSize = 32;
    const ratio = Math.max(0.3, fontSize / baseFontSize);
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    return {
      shadowStroke: Math.max(1, Math.round((primaryStyle.shadow.strokeThickness || 4) * ratio)),
      topStroke: Math.max(1, Math.round((primaryStyle.top.strokeThickness || 3) * ratio)),
      shadowOffsetY: Math.max(1, Math.round((primaryStyle.shadowOffsetY || 3) * ratio)),
    };
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    this.buttonText.text = buttonText;
    if (this.buttonShadowText) {
      this.buttonShadowText.text = buttonText;
    }
  }

  /**
   * Show continue button with fade-in animation
   * (Same as intro — uses spine-attached buttonContainer)
   */
  showButton() {
    // Show PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   * (Same as intro)
   */
  hideButton() {
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  // ===================================================================
  //  TEXT SLOTS
  // ===================================================================

  /**
   * Initialize all text slots for the free spin outro dialog.
   * Called once at init time — text containers are reused across show/hide cycles.
   */
  initializeTexts() {
    if (!this.outroSpine?.skeleton) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const secondaryStyle = PixiSettings.FONT_STYLES.SECONDARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;

    // TOTAL WIN — PRIMARY two-layer
    this.initializePrimarySlotText(
      "txt_slot_total_win",
      this.t("total_win").toUpperCase(),
      primaryStyle,
      slotText,
      76,
      "800",
    );

    // Total win value — SECONDARY, placeholder until updateTotalWinValue is called
    this.totalValueText = this.initializeSecondarySlotText(
      "txt_slot_total_value",
      "",
      secondaryStyle,
      slotText,
      86,
    );
  }

  /**
   * Create a two-layer PRIMARY text and attach it to a spine slot.
   * (Same as intro's initializePrimarySlotText)
   */
  initializePrimarySlotText(
    slotName,
    text,
    primaryStyle,
    slotText,
    fontSize,
    fontWeight = "800",
  ) {
    const slot = this.outroSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight,
    };

    // Shadow layer
    const shadowText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.shadow }),
    );
    shadowText.resolution = 2;
    shadowText.anchor.set(0.5);
    shadowText.y = primaryStyle.shadowOffsetY || 3;

    // Top layer
    const topText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.top }),
    );
    topText.resolution = 2;
    topText.anchor.set(0.5);

    container.addChild(shadowText);
    container.addChild(topText);
    this.outroSpine.addSlotObject(slotName, container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
  }

  /**
   * Create a single-layer SECONDARY text and attach it to a spine slot.
   * (Same as intro's initializeSecondarySlotText)
   */
  initializeSecondarySlotText(
    slotName,
    text,
    secondaryStyle,
    slotText,
    fontSize,
  ) {
    const slot = this.outroSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const textObj = new PIXI.Text(
      text,
      new PIXI.TextStyle({
        fontFamily: secondaryStyle.fontFamily,
        fill: secondaryStyle.fill,
        fillGradientType: secondaryStyle.fillGradientType,
        stroke: secondaryStyle.stroke,
        strokeThickness: secondaryStyle.strokeThickness,
        dropShadow: secondaryStyle.dropShadow,
        dropShadowColor: secondaryStyle.dropShadowColor,
        dropShadowDistance: secondaryStyle.dropShadowDistance,
        dropShadowBlur: secondaryStyle.dropShadowBlur,
        dropShadowAlpha: secondaryStyle.dropShadowAlpha,
        dropShadowAngle: secondaryStyle.dropShadowAngle,
        ...slotText,
        fontSize,
        fontWeight: "700",
      }),
    );
    textObj.resolution = 2;
    textObj.anchor.set(0.5);

    container.addChild(textObj);
    this.outroSpine.addSlotObject(slotName, container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);

    // Return the PIXI.Text for external updates
    return textObj;
  }

  /**
   * Update the total win value display
   * @param {string} text - Formatted win amount
   */
  updateTotalWinValue(text) {
    if (this.totalValueText) {
      this.totalValueText.text = text;
    }
  }

  // ===================================================================
  //  ANIMATION HELPERS
  // ===================================================================

  /**
   * Get animation duration from spine data
   * (Same as intro's getSpineAnimationDuration)
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.outroSpine ||
      !this.outroSpine.skeleton ||
      !this.outroSpine.skeleton.data ||
      !this.outroSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.outroSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch {
      return 1000; // Default fallback
    }
  }

  // ===================================================================
  //  ANIMATION SEQUENCE (same pattern as intro)
  // ===================================================================

  /**
   * Restart the sequence from scratch - used when tab becomes active again.
   * (Same as intro's restartSequence)
   */
  restartSequence() {
    if (this.isWaitingForClick) {
      return;
    }

    if (!this.isPlaying) {
      return;
    }

    // Clear all pending timeouts from the interrupted sequence
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset isPlaying so startSequence() guard doesn't block
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;

    // Restart cleanly
    this.startSequence();
  }

  /**
   * Start the outro animation sequence
   * (Same pattern as intro's startSequence)
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.outroSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Fade out free spin background music, play outro sound
      soundManager.fadeOut(SOUND_NAME.free_spin_background_music, 500);
      soundManager.play(SOUND_NAME.free_spin_outro);

      // Ensure final positioning check before showing
      if (
        this.outroSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Clear state — SAME ORDER AS INTRO: clearTracks → clearListeners → apply → setToSetupPose
      this.outroSpine.state.clearTracks();
      this.outroSpine.state.clearListeners();
      this.outroSpine.state.apply(this.outroSpine.skeleton);
      this.outroSpine.skeleton.setToSetupPose();

      // SKIP PROTECTION: Block skipping for the first second (matching intro pattern)
      // Show button after 1 second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play outro_in animation while STILL HIDDEN — lets GPU process new pose
      this.outroSpine.state.setAnimation(0, "free_spin_outro_in", false);

      // Show on NEXT FRAME — old warmup pose is gone, new animation starts fresh
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.outroSpine) {
          this.outroSpine.alpha = 1;
        }
      });

      // Get outro_in animation duration for timeout-based transition
      const inDuration = this.getSpineAnimationDuration("free_spin_outro_in");

      // After outro_in completes, start looping outro_idle
      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.outroSpine.state.setAnimation(0, "free_spin_outro_idle", true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  // ===================================================================
  //  USER INTERACTION (same as intro)
  // ===================================================================

  /**
   * Handle user click to close animation
   * (Same as intro's handleUserClick)
   */
  handleUserClick() {
    if (!this.isPlaying || !this.outroSpine?.state) {
      return false;
    }

    if (this.debounceTimeout) {
      return false;
    }

    if (!this.canSkipAnimation) {
      return false;
    }

    if (!this.isWaitingForClick) {
      return false;
    }

    // Create debounce timeout to block subsequent clicks
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    this.playClosingAnimation();
    return true;
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.outroSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop outro sound and fade in base game music
    soundManager.stop(SOUND_NAME.free_spin_outro);
    soundManager.currentBackgroundMusic = SOUND_NAME.background_music;
    soundManager.fadeIn(SOUND_NAME.background_music, 500);

    this.outroSpine.state.clearListeners();

    this.outroSpine.state.setAnimation(0, "free_spin_outro_out", false);
    this.outroSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "free_spin_outro_out" && this.isPlaying) {
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Finish the animation and clean up
   */
  finishAnimation() {
    if (!this.outroSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.outroSpine.alpha = 0.001;

    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop outro sound
    soundManager.stop(SOUND_NAME.free_spin_outro);

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Wait for overlay fade out to complete before removing dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(setShowFreeSpinOutro(false));
      }
    });
  }

  // ===================================================================
  //  CLEANUP (same as intro)
  // ===================================================================

  cleanup() {
    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_outro);

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Remove slot objects from spine
    if (this.outroSpine?.removeSlotObjects) {
      this.outroSpine.removeSlotObjects();
    }

    // Cleanup button
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonShadowText = null;
      this.buttonText = null;
    }

    // Cleanup text containers
    if (this.textContainers) {
      for (const container of this.textContainers) {
        container.destroy({ children: true });
      }
      this.textContainers = null;
    }

    if (this.outroSpine) {
      this.outroSpine.state?.clearListeners();
      this.outroSpine.state?.clearTracks();
      if (this.outroSpine.state) this.outroSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.outroSpine)) {
        this.container.removeChild(this.outroSpine);
      }
      this.outroSpine.destroy();
      this.outroSpine = null;
    }

    this.container = null;
    this.initialized = false;
  }
}

export default FreeSpinOutroDialogController;
